import React, { useState } from "react";
import { Link, Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, BookOpen, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import ThemeToggle from "@/components/ThemeToggle";
import TutorialSidebar from "@/tutorial/TutorialSidebar";
import { TutorialPDFExporter } from "@/tutorial/components/TutorialPDFExporter";

/**
 * Shell layout for /tutorial* routes.
 *  - Desktop ≥ md: sticky sidebar 240 px to the left
 *  - Mobile  < md: burger menu opens a left drawer (shadcn Sheet)
 */
export default function TutorialLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close mobile drawer on route change
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <TutorialPDFExporter>
      <div
        className="min-h-screen bg-background text-foreground"
        data-testid="tutorial-layout"
      >
        <header
          className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-border bg-background/85 px-3 backdrop-blur-md md:px-6"
          data-testid="tutorial-header"
        >
          {/* Mobile burger */}
          <div className="flex items-center gap-1 md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Ouvrir le sommaire du tutoriel"
                  data-testid="tutorial-mobile-menu-btn"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-72 p-0"
                data-testid="tutorial-mobile-drawer"
              >
                <SheetHeader className="sr-only">
                  <SheetTitle>Sommaire du tutoriel</SheetTitle>
                </SheetHeader>
                <TutorialSidebar currentId={id} />
              </SheetContent>
            </Sheet>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app/dashboard")}
            data-testid="tutorial-back-btn"
            className="hidden text-sm sm:inline-flex"
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Retour à l'application
          </Button>

          <Link
            to="/tutorial"
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-blue dark:text-brand-gold sm:text-sm"
            data-testid="tutorial-home-link"
          >
            <BookOpen className="h-4 w-4" />
            Aide & Tutoriel
          </Link>

          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/app/dashboard")}
              aria-label="Retour à l'application"
              className="sm:hidden"
              data-testid="tutorial-back-btn-mobile"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="flex">
          <div className="sticky top-14 hidden h-[calc(100vh-56px)] w-60 shrink-0 md:block">
            <TutorialSidebar currentId={id} />
          </div>

          <main
            className="flex-1 px-4 py-6 md:px-10 md:py-10"
            data-testid="tutorial-main"
          >
            <div className="mx-auto max-w-4xl">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </TutorialPDFExporter>
  );
}
